import { CheckCircle2, Download } from "lucide-react";
import { useOfflineEpisode } from "@/hooks/useOfflineEpisode";

interface Props {
  episodeId?: string | null;
  audioUrl?: string | null;
  className?: string;
}

/**
 * Compact "ready offline" indicator. Renders nothing when the episode isn't
 * downloaded (or there's no audio to download). When the episode is in the
 * offline cache it shows a small green badge. While the download is in
 * progress it shows a percentage.
 */
export default function OfflineReadyBadge({ episodeId, audioUrl, className = "" }: Props) {
  const { isOffline, isDownloading, progress } = useOfflineEpisode(
    episodeId || "",
    audioUrl || undefined,
  );

  if (!episodeId || !audioUrl) return null;

  if (isDownloading) {
    return (
      <span
        title={`Downloading for offline use — ${progress}%`}
        className={`inline-flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-1.5 py-0.5 border-2 border-foreground bg-muted text-foreground ${className}`}
      >
        <Download className="w-3 h-3 animate-pulse" />
        <span className="tabular-nums">{progress}%</span>
      </span>
    );
  }

  if (!isOffline) return null;

  return (
    <span
      title="Saved on this device — plays without internet"
      className={`inline-flex items-center gap-1 text-[10px] sm:text-xs font-mono font-bold px-1.5 py-0.5 border-2 border-[hsl(142,70%,25%)] bg-[hsl(142,70%,90%)] text-[hsl(142,70%,20%)] ${className}`}
    >
      <CheckCircle2 className="w-3 h-3" />
      <span>Offline</span>
    </span>
  );
}
